/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';

type GridGap = 'none' | 'sm' | 'md' | 'lg';
type GridBreakpoint = 'sm' | 'md' | 'lg';
type GridAlign = 'start' | 'center' | 'end' | 'stretch';

interface GridBaseProps {
    children: React.ReactNode;
    /** Gap between grid items: `none` (0), `sm` (8px), `md` (16px, default), or `lg` (24px). */
    gap?: GridGap;
    /** Collapse to a single column at this breakpoint and below (`down(breakpoint)`). */
    collapseAt?: GridBreakpoint;
    /** Number of columns when collapsed. Defaults to `1`. Use `2` to halve a 4-column grid on mobile instead of stacking flat. */
    collapsedColumns?: number;
    /** Cross-axis (vertical) alignment of grid items. Defaults to `stretch`. */
    align?: GridAlign;
    className?: string;
}

interface GridFixedProps extends GridBaseProps {
    /** Number of equal columns: `repeat(N, 1fr)`. */
    columns: number;
    minColumnWidth?: never;
    template?: never;
}

interface GridAutoFillProps extends GridBaseProps {
    columns?: never;
    /** Auto-fit columns with this minimum width in px: `repeat(auto-fit, minmax(Npx, 1fr))`. Cards expand to fill the row. */
    minColumnWidth: number;
    template?: never;
}

interface GridTemplateProps extends GridBaseProps {
    columns?: never;
    minColumnWidth?: never;
    /** Raw `grid-template-columns` value, e.g. `"1fr 360px"`. Escape hatch for asymmetric layouts. */
    template: string;
}

type GridProps = GridFixedProps | GridAutoFillProps | GridTemplateProps;

const GAP_PX: Record<GridGap, number> = { none: 0, sm: 8, md: 16, lg: 24 };
const ALIGN_VALUE: Record<GridAlign, string> = {
    start: 'start',
    center: 'center',
    end: 'end',
    stretch: 'stretch',
};

type StyleProps = {
    $template: string;
    $collapsedTemplate: string;
    $gap: GridGap;
    $collapseAt?: GridBreakpoint;
    $align: GridAlign;
};

const shouldForward = (prop: string) =>
    !['$template', '$collapsedTemplate', '$gap', '$collapseAt', '$align'].includes(prop);

const StyledGrid = styled('div', { shouldForwardProp: (p) => shouldForward(p as string) })<StyleProps>(
    ({ $template, $collapsedTemplate, $gap, $collapseAt, $align, theme }) => ({
        display: 'grid',
        gridTemplateColumns: $template,
        gap: GAP_PX[$gap],
        alignItems: ALIGN_VALUE[$align],
        ...($collapseAt && {
            [theme.breakpoints.down($collapseAt)]: {
                gridTemplateColumns: $collapsedTemplate,
            },
        }),
    }),
);

export function Grid(props: GridProps) {
    const { children, gap = 'md', collapseAt, collapsedColumns = 1, align = 'stretch', className } = props;

    let template: string;
    if (props.columns !== undefined) {
        template = `repeat(${props.columns}, minmax(0, 1fr))`;
    } else if (props.minColumnWidth !== undefined) {
        template = `repeat(auto-fit, minmax(${props.minColumnWidth}px, 1fr))`;
    } else {
        template = props.template;
    }

    const collapsedTemplate = `repeat(${collapsedColumns}, minmax(0, 1fr))`;

    return (
        <StyledGrid
            $template={template}
            $collapsedTemplate={collapsedTemplate}
            $gap={gap}
            $collapseAt={collapseAt}
            $align={align}
            className={className}
        >
            {children}
        </StyledGrid>
    );
}
/* eslint-enable no-comments/disallowComments */
